import { Button } from "@blurb/codex-react";
import { MantineProvider } from "@mantine/core";
import { GraphQL } from "@rpi/openapi-api";
import { IconV3 } from "@rpi/openapi-core";
import React from "react";
import { useOutlet } from "react-router-dom";

import { Pagination } from "../../../components/Pagination";
import { RpiTableContainer } from "../../../components/RpiTableContainer";
import { Heading } from "../../../components/Typography";
import { usePageShellStyles } from "../../../components/pageShellStyles";
import { SearchBar } from "../../../components/search-filter/SearchFilterBar";
import { customerColumns } from "../../../configuration/columns/customer.column";
import { useNavigateRoute } from "../../../hooks/useNavigateRoute";
import { useRpiPagination } from "../../../hooks/usePagination";
import { RpiTable } from "../../../rpi-core/table/RpiTable";
import { rpaTheme } from "../../../theme/rpaTheme";
import { SearchGeneral, SearchSort } from "../../../utils/graphql.util";

export const CustomersView: React.FC = () => {
  const { classes } = usePageShellStyles();
  const navigateRoute = useNavigateRoute();
  const outlet = useOutlet();

  const [sortInput, setSortInput] = React.useState<SearchSort<GraphQL.AdminSearchCustomersQueryVariables>>();
  const [searchGeneral, setSearchGeneral] = React.useState<SearchGeneral<GraphQL.AdminSearchCustomersQueryVariables>>();

  const pagination = useRpiPagination({ perPage: 20 });

  const { data, isLoading, error, refetch } = GraphQL.useAdminSearchCustomersQuery(
    {
      limit: pagination.perPage,
      offset: pagination.offset,
      search: {
        inputs: [],
        sort: sortInput,
        generalInput: searchGeneral,
      },
    },
    {
      onSuccess: (data) => pagination.setTotalCount(data.adminSearchCustomers.totalCount),
    }
  );
  const organizations = data?.adminSearchCustomers.data;

  React.useEffect(() => {
    if (outlet === null) refetch();
  }, [outlet]);

  return (
    <MantineProvider theme={rpaTheme}>
      <div className={classes.container} data-testid="organizations-view" aria-label="Organizations view">
        <Heading as="h1" size="6xl">
          Organizations
        </Heading>
        <div className={classes.scrollStack}>
          <div className={classes.row}>
            <div className={classes.searchSlot}>
              <SearchBar
                placeholder="Search by Public ID or Admin Email"
                isLoading={isLoading}
                onSearchSubmit={setSearchGeneral}
              />
            </div>
            <div className={classes.titleActions}>
              <Button variant="filled" intent="primary" onClick={() => navigateRoute(["customer:create"])}>
                <IconV3 icon="add" color="inherit" />
                Add new organization
              </Button>
            </div>
          </div>
          <RpiTableContainer
            data-testid="customers-table"
            footer={<Pagination isLoading={isLoading} pagination={pagination} label="Organizations" />}
          >
            <RpiTable
              emptyMessage={searchGeneral ? "No organizations found." : "Organizations will appear here."}
              isLoading={isLoading}
              error={error}
              perPage={organizations?.length}
              data={organizations}
              columns={customerColumns}
              withSortBy={{ onChange: setSortInput }}
            />
          </RpiTableContainer>
        </div>
      </div>
      {outlet}
    </MantineProvider>
  );
};
